/**
 * Espace d'administration — écran « Scénarios ».
 *
 * Les scénarios votés restent en lecture seule : toute l'application repose sur
 * la confrontation du réel à ce qui a été voté, et un scénario 1 modifiable
 * ferait changer de sens la ligne « Trajectoire scénario 1 » de la synthèse
 * sans que personne ne s'en aperçoive. Modifier engendre donc un **scénario
 * dérivé**, nommé, daté, qui garde le lien vers son parent.
 *
 * L'éditeur est une **grille unique** : tous les inducteurs sur une page, une
 * ligne chacun, une colonne par exercice. Un scénario se lit et s'arbitre en
 * une vue.
 *
 * Aucune formule n'est réécrite ici. `evaluerScenario` est pure : l'interface
 * appelle exactement la fonction du serveur, et le total de pied de grille est
 * donc le même que celui qui sera enregistré.
 */

import {
  MODES_PILOTE,
  NATURES_EXCEPTIONNELLES,
  TRAJECTOIRES_SALARIALES,
  besoinFondsRoulement,
  decrirePilote,
  entier,
  euros,
  eurosSigne,
  evaluerScenario,
  horodatageFr,
  keuros,
  passageResultatTresorerie,
  poste,
  resoudrePilote,
  type DefinitionScenario,
  type Exercice,
  type LigneExceptionnelle,
  type ModePilote,
  type NatureExceptionnelle,
  type Pilote,
} from '@scc/core';
import {
  creerScenario,
  enregistrerScenario,
  listerScenarios,
  supprimerScenario,
  type Charge,
  type Identite,
  type ScenarioEnregistre,
} from '../api.js';
import { h, vider } from '../dom.js';
import { badge, enteteSection, insight, noteLecture, regleAccent } from '../composants.js';

/** Libellés des modes de saisie, dans l'ordre du cycle de la pastille. */
const LIBELLE_MODE: Readonly<Record<ModePilote, string>> = {
  annee_par_annee: 'par an',
  cible: 'cible',
  constant: 'constant',
  indexe: 'indexé',
};

const LIBELLE_NATURE: Readonly<Record<NatureExceptionnelle, string>> = {
  produit: 'Produit exceptionnel',
  charge: 'Charge exceptionnelle',
  investissement: 'Investissement',
};

/** Clé d'URL déduite d'un libellé : minuscules, tirets, sans diacritiques. */
function cleDepuisLibelle(libelle: string): string {
  const sansAccent = libelle.normalize('NFD').replace(/[̀-ͯ]/gu, '');
  const brute = sansAccent
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, '-')
    .replace(/^-+|-+$/gu, '')
    .slice(0, 34);
  return brute.length >= 3 ? brute : `scenario-${brute}`;
}

/**
 * Convertit un pilote d'un mode à l'autre en conservant ce qui a du sens.
 * Passer de « par an » à « constant » retient la première valeur plutôt que de
 * remettre à zéro : un changement de mode n'est pas un effacement.
 */
function convertirPilote(
  p: Pilote,
  vers: ModePilote,
  exercices: readonly Exercice[],
  depart: number,
): Pilote {
  const serie = resoudrePilote(p, exercices, depart);
  const premiere = serie[0] ?? 0;
  const derniere = serie[serie.length - 1] ?? premiere;
  const dernierExercice = exercices[exercices.length - 1] ?? 0;

  switch (vers) {
    case 'annee_par_annee':
      return { mode: 'annee_par_annee', valeurs: serie };
    case 'cible':
      return { mode: 'cible', cible: derniere, exerciceCible: dernierExercice };
    case 'constant':
      return { mode: 'constant', valeur: premiere };
    case 'indexe': {
      // Taux implicite entre la première et la dernière valeur de la série.
      const pas = exercices.length - 1;
      const taux =
        pas > 0 && premiere > 0 ? Math.round(((derniere / premiere) ** (1 / pas) - 1) * 1000) / 1000 : 0;
      return { mode: 'indexe', valeur: premiere, taux: Math.max(-1, Math.min(1, taux)) };
    }
  }
}

export function vueScenarios(d: Charge, identite: Identite): HTMLElement {
  const estDirection = identite.role === 'administration';
  const exercices = d.modeleDerive.exercices;

  /** Brouillon en cours d'édition ; `null` quand aucun éditeur n'est ouvert. */
  let brouillon: DefinitionScenario | null = null;
  /** Vrai quand le brouillon n'existe pas encore côté serveur. */
  let creation = false;
  let enregistres: readonly ScenarioEnregistre[] = [];

  const zoneListe = h('div', { classe: 'liste-scenarios' });
  const zoneEditeur = h('div', {});
  const retour = h('div', {});

  const dire = (texte: string, ok: boolean): void => {
    vider(retour);
    retour.appendChild(h('p', { classe: `retour-editeur ${ok ? 'ok' : 'ko'}`, role: 'status' }, texte));
  };

  // ── Évaluation locale ──────────────────────────────────────────────────
  //
  // Le noyau est pur : on appelle la même fonction que le serveur, et le pied
  // de grille annonce donc exactement ce qui sera enregistré.
  const evaluer = (def: DefinitionScenario) =>
    evaluerScenario(def, {
      ...d.contexteScenario,
      masseSalariale: d.massesParTrajectoire[def.trajectoireSalariale] ?? {},
    });

  /** Trésorerie du brouillon, sur la position de BFR mesurée à l'ouverture. */
  const tresorerieDe = (evalue: ReturnType<typeof evaluer>) => {
    const bfr = besoinFondsRoulement({
      assiettes: evalue.detail.map((x) => ({ exercice: x.exercice, produits: x.totalProduits })),
      ratio: d.tresorerie.ratio,
      bfrOuverture: d.tresorerie.bfrOuverture,
    });
    return passageResultatTresorerie({
      tresorerieOuverture: d.tresorerie.soldeOuverture,
      exercices: evalue.detail.map((x) => ({
        exercice: x.exercice,
        resultat: x.resultat,
        amortissements: x.amortissements,
        investissements: x.investissements,
      })),
      bfr,
    });
  };

  // ── Liste des scénarios ────────────────────────────────────────────────
  const ouvrir = (def: DefinitionScenario, nouveau: boolean): void => {
    brouillon = structuredClone(def);
    creation = nouveau;
    vider(retour);
    dessinerEditeur();
    dessinerListe();
    zoneEditeur.scrollIntoView({ block: 'start' });
  };

  const deriver = (parent: string, libelleParent: string, base: DefinitionScenario): void => {
    const libelle = `${libelleParent} — variante`;
    ouvrir(
      {
        ...structuredClone(base),
        cle: cleDepuisLibelle(`${libelleParent}-variante`),
        libelle,
        parent,
        commentaire: null,
      },
      true,
    );
  };

  const dessinerListe = (): void => {
    vider(zoneListe);

    // Scénarios de référence : lecture seule, dérivables.
    for (const t of d.trajectoires.filter((x) => ['sc1', 'sc2', 'sc3'].includes(x.cle))) {
      const carte = h(
        'div',
        { classe: 'carte-scenario' },
        h('span', { classe: 'nom' }, t.libelle),
        badge('neutre', 'Voté — lecture seule'),
        h(
          'p',
          { classe: 'filiation' },
          `Réserve finale ${euros(t.reserveFinale)} · ${entier(t.annees[t.annees.length - 1]?.adherents ?? 0)} adhérents en ${t.annees[t.annees.length - 1]?.annee ?? ''}`,
        ),
        estDirection
          ? h(
              'div',
              { classe: 'actions' },
              h(
                'button',
                {
                  classe: 'btn-action',
                  type: 'button',
                  surClick: () => {
                    deriver(t.cle, t.libelle, { ...d.modeleDerive, parent: t.cle });
                  },
                },
                'Dériver',
              ),
            )
          : null,
      );
      zoneListe.appendChild(carte);
    }

    // Scénarios dérivés.
    for (const s of enregistres) {
      const evalue = evaluer(s.definition);
      const derniere = evalue.trajectoire.annees[evalue.trajectoire.annees.length - 1];
      const carte = h(
        'div',
        { classe: `carte-scenario ${brouillon?.cle === s.cle ? 'actif' : ''}` },
        h('span', { classe: 'nom' }, s.libelle),
        badge('favorable', 'Dérivé'),
        h(
          'p',
          { classe: 'filiation' },
          `Dérivé de ${s.parent ?? '—'} · réserve finale ${euros(evalue.trajectoire.reserveFinale)} · ` +
            `${entier(derniere?.adherents ?? 0)} adhérents en ${derniere?.annee ?? ''}`,
        ),
        h('p', { classe: 'signature' }, `${s.modifiePar} — ${horodatageFr(s.modifieLe)}`),
        estDirection
          ? h(
              'div',
              { classe: 'actions' },
              h(
                'button',
                { classe: 'btn-action', type: 'button', surClick: () => { ouvrir(s.definition, false); } },
                'Modifier',
              ),
              h(
                'button',
                {
                  classe: 'btn-action',
                  type: 'button',
                  surClick: () => { deriver(s.cle, s.libelle, s.definition); },
                },
                'Dériver',
              ),
              h(
                'button',
                {
                  classe: 'btn-action',
                  type: 'button',
                  surClick: (ev) => {
                    const b = ev.currentTarget as HTMLButtonElement;
                    void (async () => {
                      b.disabled = true;
                      try {
                        await supprimerScenario(s.cle);
                        if (brouillon?.cle === s.cle) {
                          brouillon = null;
                          vider(zoneEditeur);
                        }
                        await recharger();
                        dire(`Scénario « ${s.libelle} » supprimé.`, true);
                      } catch (e) {
                        dire(e instanceof Error ? e.message : 'Suppression refusée.', false);
                      } finally {
                        b.disabled = false;
                      }
                    })();
                  },
                },
                'Supprimer',
              ),
            )
          : null,
      );
      zoneListe.appendChild(carte);
    }

    if (enregistres.length === 0) {
      zoneListe.appendChild(
        h(
          'div',
          { classe: 'carte-scenario' },
          h('span', { classe: 'nom' }, 'Aucun scénario dérivé'),
          h(
            'p',
            { classe: 'filiation' },
            estDirection
              ? 'Dérivez un scénario voté pour éprouver une hypothèse : un investissement, une subvention ponctuelle, une autre cible d’effectifs.'
              : 'La direction n’a encore enregistré aucun scénario dérivé.',
          ),
        ),
      );
    }
  };

  // ── Éditeur ────────────────────────────────────────────────────────────
  const dessinerEditeur = (): void => {
    vider(zoneEditeur);
    const def = brouillon;
    if (!def) return;

    const evalue = evaluer(def);
    const passage = tresorerieDe(evalue);

    /** Applique une modification au brouillon et redessine. */
    const modifier = (suite: Partial<DefinitionScenario>): void => {
      brouillon = { ...def, ...suite };
      dessinerEditeur();
    };

    // — Identité du scénario —
    const champs = h(
      'div',
      { classe: 'editeur-champs' },
      h(
        'label',
        {},
        'Nom du scénario',
        h('input', {
          type: 'text',
          value: def.libelle,
          maxlength: 120,
          surChange: (ev) => {
            const v = (ev.currentTarget as HTMLInputElement).value;
            modifier(creation ? { libelle: v, cle: cleDepuisLibelle(v) } : { libelle: v });
          },
        }),
      ),
      h(
        'label',
        {},
        'Clé',
        h('input', {
          type: 'text',
          value: def.cle,
          readonly: !creation,
          maxlength: 40,
          surChange: (ev) => { modifier({ cle: (ev.currentTarget as HTMLInputElement).value }); },
        }),
      ),
      h('label', {}, 'Dérivé de', h('input', { type: 'text', value: def.parent ?? '—', readonly: true })),
      h(
        'label',
        {},
        'Trajectoire salariale',
        h(
          'select',
          {
            surChange: (ev) => {
              modifier({ trajectoireSalariale: (ev.currentTarget as HTMLSelectElement).value });
            },
          },
          ...TRAJECTOIRES_SALARIALES.map((t) =>
            h(
              'option',
              { value: t.cle, ...(t.cle === def.trajectoireSalariale ? { selected: true } : {}) },
              t.libelle,
            ),
          ),
        ),
      ),
      h(
        'label',
        {},
        'Régime de charges',
        h(
          'select',
          {
            surChange: (ev) => {
              const v = (ev.currentTarget as HTMLSelectElement).value;
              modifier({
                charges:
                  v === 'global'
                    ? { mode: 'global', progression: 0.025 }
                    : { mode: 'detaille', progressionAutresCharges: 0.025 },
              });
            },
          },
          h(
            'option',
            { value: 'global', ...(def.charges.mode === 'global' ? { selected: true } : {}) },
            'Global — charges indexées d’un taux unique',
          ),
          h(
            'option',
            { value: 'detaille', ...(def.charges.mode === 'detaille' ? { selected: true } : {}) },
            'Détaillé — masse salariale du moteur de paie',
          ),
        ),
      ),
      h(
        'label',
        {},
        def.charges.mode === 'global' ? 'Progression des charges' : 'Progression des autres charges',
        h('input', {
          type: 'number',
          step: 0.1,
          min: -100,
          max: 100,
          value: (
            (def.charges.mode === 'global' ? def.charges.progression : def.charges.progressionAutresCharges) * 100
          ).toFixed(1),
          surChange: (ev) => {
            const t = Number((ev.currentTarget as HTMLInputElement).value) / 100;
            if (!Number.isFinite(t)) return;
            modifier({
              charges:
                def.charges.mode === 'global'
                  ? { mode: 'global', progression: t }
                  : { mode: 'detaille', progressionAutresCharges: t },
            });
          },
        }),
      ),
    );

    // — Grille des inducteurs —
    /**
     * Une ligne de la grille : un pilote, sa pastille de mode et ses cellules.
     * Le mode commande la forme de la ligne — c'est exactement ce que la
     * pastille annonce.
     */
    const ligne = (
      libelle: string,
      p: Pilote,
      depart: number,
      unite: 'euros' | 'effectif',
      appliquer: (p: Pilote) => void,
    ): HTMLElement => {
      const serie = resoudrePilote(p, exercices, depart);
      const rendre = (v: number): string => (unite === 'effectif' ? entier(v) : euros(v));

      const pastille = h(
        'button',
        {
          classe: `mode-pilote mode-${p.mode}`,
          type: 'button',
          'aria-label': `Mode de saisie de « ${libelle} » : ${LIBELLE_MODE[p.mode]}. Changer.`,
          disabled: !estDirection,
          surClick: () => {
            const i = MODES_PILOTE.indexOf(p.mode);
            const suivant = MODES_PILOTE[(i + 1) % MODES_PILOTE.length] ?? 'constant';
            appliquer(convertirPilote(p, suivant, exercices, depart));
          },
        },
        LIBELLE_MODE[p.mode],
      );

      const cellules: HTMLElement[] = [];
      if (p.mode === 'annee_par_annee') {
        exercices.forEach((_, i) => {
          cellules.push(
            h(
              'td',
              {},
              h('input', {
                type: 'number',
                step: unite === 'effectif' ? 1 : 0.01,
                value: String(serie[i] ?? 0),
                'aria-label': `${libelle}, exercice ${exercices[i] ?? ''}`,
                readonly: !estDirection,
                surChange: (ev) => {
                  const v = Number((ev.currentTarget as HTMLInputElement).value);
                  if (!Number.isFinite(v)) return;
                  const valeurs = [...serie];
                  valeurs[i] = v;
                  appliquer({ mode: 'annee_par_annee', valeurs });
                },
              }),
            ),
          );
        });
      } else if (p.mode === 'cible') {
        cellules.push(
          h(
            'td',
            { colspan: exercices.length - 1, classe: 'cellule-etalee' },
            `${rendre(depart)} au départ, interpolation linéaire — ${serie
              .slice(0, -1)
              .map((v, i) => `${exercices[i] ?? ''} : ${rendre(v)}`)
              .join(' · ')}`,
          ),
          h(
            'td',
            {},
            h('input', {
              type: 'number',
              step: unite === 'effectif' ? 1 : 0.01,
              value: String(p.cible),
              'aria-label': `Cible de « ${libelle} »`,
              readonly: !estDirection,
              surChange: (ev) => {
                const v = Number((ev.currentTarget as HTMLInputElement).value);
                if (!Number.isFinite(v)) return;
                appliquer({ mode: 'cible', cible: v, exerciceCible: p.exerciceCible });
              },
            }),
          ),
        );
      } else if (p.mode === 'constant') {
        cellules.push(
          h(
            'td',
            { colspan: exercices.length },
            h('input', {
              type: 'number',
              step: unite === 'effectif' ? 1 : 0.01,
              value: String(p.valeur),
              'aria-label': `${libelle}, valeur constante`,
              readonly: !estDirection,
              surChange: (ev) => {
                const v = Number((ev.currentTarget as HTMLInputElement).value);
                if (!Number.isFinite(v)) return;
                appliquer({ mode: 'constant', valeur: v });
              },
            }),
          ),
        );
      } else {
        cellules.push(
          h(
            'td',
            { colspan: exercices.length, classe: 'cellule-etalee' },
            `${rendre(p.valeur)} au départ, `,
            h('input', {
              type: 'number',
              step: 0.1,
              value: (p.taux * 100).toFixed(1),
              'aria-label': `Taux annuel de « ${libelle} », en pourcentage`,
              readonly: !estDirection,
              surChange: (ev) => {
                const t = Number((ev.currentTarget as HTMLInputElement).value) / 100;
                if (!Number.isFinite(t)) return;
                appliquer({ mode: 'indexe', valeur: p.valeur, taux: t });
              },
            }),
            ` % par an — ${serie.map((v) => rendre(v)).join(' · ')}`,
          ),
        );
      }

      return h(
        'tr',
        {},
        h('th', { scope: 'row' }, libelle),
        h('td', {}, pastille),
        ...cellules,
      );
    };

    const lignesRessources = Object.entries(def.ressources).map(([code, p]) =>
      ligne(poste(code)?.libelle ?? code, p, 0, 'euros', (nouveau) => {
        modifier({ ressources: { ...def.ressources, [code]: nouveau } });
      }),
    );

    const totalLigne = (
      classe: string,
      libelle: string,
      valeurs: readonly number[],
      signe = false,
    ): HTMLElement =>
      h(
        'tr',
        { classe },
        h('th', { scope: 'row', colspan: 2 }, libelle),
        ...valeurs.map((v) => h('td', {}, signe ? eurosSigne(v) : keuros(v))),
      );

    const grille = h(
      'div',
      { classe: 'enveloppe-table' },
      h(
        'table',
        { classe: 'table-scenario' },
        h('caption', { classe: 'invisible' }, `Grille du scénario ${def.libelle}`),
        h(
          'thead',
          {},
          h(
            'tr',
            {},
            h('th', { scope: 'col' }, 'Inducteur'),
            h('th', { scope: 'col' }, 'Mode'),
            ...exercices.map((a) => h('th', { scope: 'col' }, String(a))),
          ),
        ),
        h(
          'tbody',
          {},
          h('tr', { classe: 'groupe' }, h('th', { colspan: exercices.length + 2 }, 'Produits')),
          ligne(
            'Adhérents payeurs',
            def.adherents,
            d.contexteScenario.payeursExerciceCourant,
            'effectif',
            (p) => { modifier({ adherents: p }); },
          ),
          ligne(
            'Produit moyen par adhérent',
            def.produitMoyen,
            d.contexteScenario.produitMoyenExerciceCourant,
            'euros',
            (p) => { modifier({ produitMoyen: p }); },
          ),
          ...lignesRessources,
          h(
            'tr',
            {},
            h('th', { scope: 'row', colspan: 2 }, 'Cotisations engendrées'),
            ...evalue.detail.map((x) => h('td', {}, keuros(x.cotisations))),
          ),
          h('tr', { classe: 'groupe' }, h('th', { colspan: exercices.length + 2 }, 'Charges')),
          h(
            'tr',
            {},
            h('th', { scope: 'row', colspan: 2 }, 'Masse salariale chargée'),
            ...evalue.detail.map((x) =>
              h('td', {}, x.masseSalariale > 0 ? keuros(x.masseSalariale) : 'incluse ci-dessous'),
            ),
          ),
          h(
            'tr',
            {},
            h('th', { scope: 'row', colspan: 2 }, 'Autres charges'),
            ...evalue.detail.map((x) => h('td', {}, keuros(x.autresCharges))),
          ),
          h(
            'tr',
            {},
            h('th', { scope: 'row', colspan: 2 }, 'Amortissements'),
            ...evalue.detail.map((x) => h('td', {}, keuros(x.amortissements))),
          ),
          totalLigne('total', 'Total des produits', evalue.detail.map((x) => x.totalProduits)),
          totalLigne('total', 'Total des charges', evalue.detail.map((x) => x.totalCharges)),
          totalLigne('total', 'Résultat', evalue.detail.map((x) => x.resultat), true),
          totalLigne('tresorerie', 'Trésorerie de clôture', passage.map((p) => p.tresorerieCloture)),
          totalLigne(
            'tresorerie',
            'Réserve de clôture',
            evalue.trajectoire.annees.map((a) => a.reserve),
          ),
        ),
      ),
    );

    // — Lignes exceptionnelles —
    const lignesExc = h(
      'div',
      { classe: 'enveloppe-table' },
      h(
        'table',
        { classe: 'table-scenario' },
        h('caption', { classe: 'invisible' }, 'Lignes exceptionnelles du scénario'),
        h(
          'thead',
          {},
          h(
            'tr',
            {},
            h('th', { scope: 'col' }, 'Libellé'),
            h('th', { scope: 'col' }, 'Nature'),
            h('th', { scope: 'col' }, 'Exercice'),
            h('th', { scope: 'col' }, 'Montant'),
            h('th', { scope: 'col' }, 'Amortissement'),
            h('th', { scope: 'col' }, ''),
          ),
        ),
        h(
          'tbody',
          {},
          ...(def.lignesExceptionnelles.length === 0
            ? [
                h(
                  'tr',
                  {},
                  h('th', { scope: 'row', colspan: 6, classe: 'cellule-etalee' },
                    'Aucune opération ponctuelle. Un investissement sort de la trésorerie l’année de ' +
                      'l’acquisition et grève le résultat par son amortissement ; une subvention ponctuelle ' +
                      'ne se reconduit pas.'),
                ),
              ]
            : def.lignesExceptionnelles.map((l) => {
                const remplacer = (suite: Partial<LigneExceptionnelle>): void => {
                  modifier({
                    lignesExceptionnelles: def.lignesExceptionnelles.map((x) =>
                      x.id === l.id ? { ...x, ...suite } : x,
                    ),
                  });
                };
                return h(
                  'tr',
                  {},
                  h(
                    'th',
                    { scope: 'row' },
                    h('input', {
                      type: 'text',
                      value: l.libelle,
                      'aria-label': 'Libellé de la ligne exceptionnelle',
                      readonly: !estDirection,
                      surChange: (ev) => { remplacer({ libelle: (ev.currentTarget as HTMLInputElement).value }); },
                    }),
                  ),
                  h(
                    'td',
                    {},
                    h(
                      'select',
                      {
                        'aria-label': 'Nature de la ligne exceptionnelle',
                        disabled: !estDirection,
                        surChange: (ev) => {
                          remplacer({ nature: (ev.currentTarget as HTMLSelectElement).value as NatureExceptionnelle });
                        },
                      },
                      ...NATURES_EXCEPTIONNELLES.map((n) =>
                        h('option', { value: n, ...(n === l.nature ? { selected: true } : {}) }, LIBELLE_NATURE[n]),
                      ),
                    ),
                  ),
                  h(
                    'td',
                    {},
                    h(
                      'select',
                      {
                        'aria-label': 'Exercice de la ligne exceptionnelle',
                        disabled: !estDirection,
                        surChange: (ev) => {
                          remplacer({ exercice: Number((ev.currentTarget as HTMLSelectElement).value) });
                        },
                      },
                      ...exercices.map((a) =>
                        h('option', { value: String(a), ...(a === l.exercice ? { selected: true } : {}) }, String(a)),
                      ),
                    ),
                  ),
                  h(
                    'td',
                    {},
                    h('input', {
                      type: 'number',
                      step: 100,
                      min: 0,
                      value: String(l.montant),
                      'aria-label': 'Montant de la ligne exceptionnelle',
                      readonly: !estDirection,
                      surChange: (ev) => {
                        const v = Number((ev.currentTarget as HTMLInputElement).value);
                        if (Number.isFinite(v)) remplacer({ montant: Math.max(0, v) });
                      },
                    }),
                  ),
                  h(
                    'td',
                    {},
                    l.nature === 'investissement'
                      ? h('input', {
                          type: 'number',
                          step: 1,
                          min: 0,
                          max: 30,
                          value: String(l.dureeAmortissement),
                          'aria-label': 'Durée d’amortissement, en années',
                          readonly: !estDirection,
                          surChange: (ev) => {
                            const v = Number((ev.currentTarget as HTMLInputElement).value);
                            if (Number.isFinite(v)) remplacer({ dureeAmortissement: Math.max(0, Math.round(v)) });
                          },
                        })
                      : h('span', { classe: 'cellule-etalee' }, 'sans objet'),
                  ),
                  h(
                    'td',
                    {},
                    estDirection
                      ? h(
                          'button',
                          {
                            classe: 'btn-action',
                            type: 'button',
                            surClick: () => {
                              modifier({
                                lignesExceptionnelles: def.lignesExceptionnelles.filter((x) => x.id !== l.id),
                              });
                            },
                          },
                          'Retirer',
                        )
                      : '',
                  ),
                );
              })),
        ),
      ),
    );

    const ajouterLigne = (nature: NatureExceptionnelle): void => {
      modifier({
        lignesExceptionnelles: [
          ...def.lignesExceptionnelles,
          {
            id: `l${def.lignesExceptionnelles.length + 1}-${nature}`,
            libelle: LIBELLE_NATURE[nature],
            nature,
            exercice: exercices[0] ?? d.meta.exerciceCourant,
            montant: 0,
            dureeAmortissement: nature === 'investissement' ? 5 : 0,
          } satisfies LigneExceptionnelle,
        ],
      });
    };

    const actions = h(
      'div',
      { classe: 'barre-actions' },
      h('span', { classe: 'titre' }, creation ? 'Nouveau scénario' : 'Modifier'),
      ...(estDirection
        ? [
            h(
              'button',
              { classe: 'btn-action', type: 'button', surClick: () => { ajouterLigne('investissement'); } },
              'Ajouter un investissement',
            ),
            h(
              'button',
              { classe: 'btn-action', type: 'button', surClick: () => { ajouterLigne('produit'); } },
              'Ajouter une subvention ponctuelle',
            ),
            h(
              'button',
              { classe: 'btn-action', type: 'button', surClick: () => { ajouterLigne('charge'); } },
              'Ajouter une charge ponctuelle',
            ),
            h(
              'button',
              {
                classe: 'btn-action',
                type: 'button',
                surClick: (ev) => {
                  const b = ev.currentTarget as HTMLButtonElement;
                  void (async () => {
                    const aEnregistrer = brouillon;
                    if (!aEnregistrer) return;
                    b.disabled = true;
                    try {
                      if (creation) await creerScenario(aEnregistrer);
                      else await enregistrerScenario(aEnregistrer);
                      creation = false;
                      await recharger();
                      dire(
                        `Scénario « ${aEnregistrer.libelle} » enregistré. Il apparaît désormais dans les ` +
                          'trajectoires, la trésorerie et le point mort — rechargez la page pour l’y voir.',
                        true,
                      );
                    } catch (e) {
                      dire(e instanceof Error ? e.message : 'Enregistrement refusé.', false);
                    } finally {
                      b.disabled = false;
                    }
                  })();
                },
              },
              'Enregistrer',
            ),
            h(
              'button',
              {
                classe: 'btn-action',
                type: 'button',
                surClick: () => {
                  brouillon = null;
                  vider(zoneEditeur);
                  dessinerListe();
                },
              },
              'Fermer',
            ),
          ]
        : []),
    );

    const dernier = passage[passage.length - 1];
    const resume = insight(
      (dernier?.sousSeuil ?? false) ? 'warning' : 'positive',
      (dernier?.sousSeuil ?? false) ? '⚠' : '✓',
      `**${def.libelle}.** ${decrirePilote(def.adherents, 'effectif')} pour les adhérents, ` +
        `${decrirePilote(def.produitMoyen, 'euros')} pour le produit moyen. ` +
        `Réserve finale ${euros(evalue.trajectoire.reserveFinale)}, trésorerie de clôture ` +
        `${euros(dernier?.tresorerieCloture ?? 0)} contre un seuil de sécurité de ${euros(dernier?.seuil ?? 0)}. ` +
        `${evalue.trajectoire.nbExercicesDeficitaires} exercice(s) déficitaire(s) sur la période` +
        (evalue.trajectoire.retourEquilibre !== null
          ? `, retour à l’équilibre en ${evalue.trajectoire.retourEquilibre}.`
          : ', sans retour à l’équilibre.'),
    );

    zoneEditeur.appendChild(regleAccent(`Éditeur — ${def.libelle}`));
    zoneEditeur.appendChild(champs);
    zoneEditeur.appendChild(actions);
    zoneEditeur.appendChild(retour);
    zoneEditeur.appendChild(grille);
    zoneEditeur.appendChild(
      noteLecture(
        'Le pied de grille se recalcule à chaque frappe avec la fonction même du serveur : le résultat ' +
          'affiché est celui qui sera enregistré. Les montants sont arrondis au millier à l’écran, jamais ' +
          'dans le calcul.',
      ),
    );
    zoneEditeur.appendChild(h('h3', { classe: 'card' }, 'Opérations ponctuelles'));
    zoneEditeur.appendChild(lignesExc);
    zoneEditeur.appendChild(resume);
  };

  // ── Chargement ─────────────────────────────────────────────────────────
  const recharger = async (): Promise<void> => {
    try {
      const r = await listerScenarios();
      enregistres = r.scenarios;
    } catch {
      enregistres = [];
    }
    dessinerListe();
  };

  void recharger();

  return h(
    'div',
    { classe: 'page', id: 'page-scenarios', role: 'region', 'aria-label': 'Scénarios' },
    h(
      'section',
      {},
      enteteSection(1, 'Scénarios', 'Construire, dériver et arbitrer une trajectoire'),
      h(
        'p',
        { classe: 'text-block' },
        '**Les scénarios votés ne se modifient pas.** Toute l’application confronte le réel à ce qui a été ' +
          'voté : rendre le scénario 1 modifiable ferait changer de sens la ligne « Trajectoire scénario 1 » ' +
          'de la synthèse sans que personne ne s’en aperçoive. Modifier engendre donc un scénario **dérivé**, ' +
          'nommé et daté, qui garde le lien vers son parent — et qui apparaît ensuite partout : trajectoires, ' +
          'trésorerie, point mort, budget.',
      ),
      estDirection
        ? h(
            'div',
            { classe: 'barre-actions' },
            h('span', { classe: 'titre' }, 'Créer'),
            h(
              'button',
              {
                classe: 'btn-action',
                type: 'button',
                surClick: () => {
                  deriver('sc3', 'Scénario de référence', d.modeleDerive);
                },
              },
              'Nouveau scénario, dérivé de la référence',
            ),
          )
        : insight('info', 'ℹ', 'La création et la modification de scénarios sont réservées à la direction.'),
      zoneListe,
      zoneEditeur,
    ),
  );
}
